import { useEffect } from "react";
import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { motion } from "framer-motion";

import About from "./About";
import Certificates from "./Certificates";
import Contact from "./Contact";

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    document
      .getElementById(sectionId)
      ?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  };

  useEffect(() => {
    const sectionId = location.state?.scrollTo;

    if (!sectionId) {
      return;
    }

    const timeout = setTimeout(() => {
      scrollToSection(sectionId);

      navigate("/", {
        replace: true,
        state: {},
      });
    }, 150);

    return () => clearTimeout(timeout);
  }, [location.state, navigate]);

  return (
    <main className="overflow-hidden bg-zinc-50 text-zinc-950">
      {/* Hero section */}
      <section
        id="home"
        className="relative flex min-h-screen scroll-mt-16 items-center overflow-hidden border-b border-zinc-200"
      >
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-50 via-zinc-100 to-slate-200" />

        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(24,24,27,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(24,24,27,0.06) 1px, transparent 1px)",
            backgroundSize: "42px 42px",
          }}
        />

        {/* Animated background shapes */}
        <motion.div
          aria-hidden="true"
          className="absolute -left-24 top-20 h-80 w-80 rounded-full bg-indigo-300/30 blur-3xl"
          animate={{
            x: [0, 80, 20, 0],
            y: [0, 30, 100, 0],
            scale: [1, 1.15, 0.95, 1],
          }}
          transition={{
            duration: 16,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        <motion.div
          aria-hidden="true"
          className="absolute -right-24 bottom-10 h-96 w-96 rounded-full bg-sky-300/30 blur-3xl"
          animate={{
            x: [0, -70, -20, 0],
            y: [0, -80, -30, 0],
            scale: [1, 0.9, 1.15, 1],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        <motion.div
          aria-hidden="true"
          className="absolute right-[30%] top-16 h-52 w-52 rounded-full bg-violet-300/20 blur-3xl"
          animate={{
            x: [0, 30, 0],
            y: [0, 70, 0],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Hero content */}
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 py-28 sm:px-10 lg:px-16">
          <div className="grid items-center gap-14 lg:grid-cols-[1.2fr_0.8fr]">
            {/* Main text */}
            <motion.div
              initial={{
                opacity: 0,
                y: 28,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.75,
                ease: "easeOut",
              }}
              className="max-w-4xl"
            >
              <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-white/70 px-4 py-2 text-sm font-medium text-zinc-700 shadow-sm backdrop-blur-md">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />

                Seeking graduate software engineering
                opportunities
              </div>

              <p className="mb-5 text-sm font-semibold uppercase tracking-[0.24em] text-indigo-600">
                Backend-focused software developer
              </p>

              <h1 className="max-w-5xl text-5xl font-semibold leading-[1.05] tracking-tight text-zinc-950 sm:text-6xl lg:text-7xl">
                Hi, I’m Kacper.

                <span className="mt-2 block text-zinc-500">
                  I build dependable software systems.
                </span>
              </h1>

              <p className="mt-7 max-w-2xl text-lg leading-8 text-zinc-600 sm:text-xl">
                I’m a recent MSc Computer Science graduate
                from University College Dublin. I build backend services,
                data-driven applications, and secure
                full-stack systems using Python,
                JavaScript, TypeScript, SQL, cloud
                technologies, and applied AI.
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/projects/home"
                  className="inline-flex items-center rounded-xl bg-zinc-950 px-6 py-3.5 font-semibold text-white shadow-lg shadow-zinc-950/10 transition duration-200 hover:-translate-y-0.5 hover:bg-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-400 focus:ring-offset-2"
                >
                  View my work
                </Link>

                <button
                  type="button"
                  onClick={() =>
                    scrollToSection("about")
                  }
                  className="group inline-flex items-center gap-2 rounded-xl border border-zinc-300 bg-white/80 px-6 py-3.5 font-semibold text-zinc-900 shadow-sm backdrop-blur-md transition duration-200 hover:-translate-y-0.5 hover:border-zinc-400 hover:bg-white focus:outline-none focus:ring-2 focus:ring-zinc-300 focus:ring-offset-2"
                >
                  More about me

                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    className="transition-transform duration-200 group-hover:translate-y-0.5"
                  >
                    <path
                      d="M12 5V19M12 19L6 13M12 19L18 13"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              <div className="mt-14 flex flex-wrap gap-x-8 gap-y-4 border-t border-zinc-300/70 pt-7 text-sm font-medium text-zinc-600">
                <span>Python</span>
                <span>FastAPI</span>
                <span>React</span>
                <span>TypeScript</span>
                <span>Java</span>
                <span>SQL</span>
                <span>AWS</span>
                <span>Docker</span>
                <span>Machine Learning</span>
              </div>
            </motion.div>

            {/* Animated code card */}
            <motion.div
              initial={{
                opacity: 0,
                x: 40,
                rotate: 2,
              }}
              animate={{
                opacity: 1,
                x: 0,
                rotate: 0,
              }}
              transition={{
                delay: 0.2,
                duration: 0.8,
              }}
              className="relative hidden lg:block"
            >
              <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-indigo-300/30 to-sky-300/30 blur-2xl" />

              <motion.div
                animate={{
                  y: [0, -10, 0],
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="relative overflow-hidden rounded-3xl border border-white/70 bg-zinc-950 shadow-2xl shadow-zinc-500/20"
              >
                <div className="flex items-center gap-2 border-b border-zinc-800 px-5 py-4">
                  <span className="h-3 w-3 rounded-full bg-red-400" />
                  <span className="h-3 w-3 rounded-full bg-amber-400" />
                  <span className="h-3 w-3 rounded-full bg-emerald-400" />

                  <span className="ml-3 text-xs text-zinc-500">
                    developer.js
                  </span>
                </div>

                <div className="space-y-4 p-7 font-mono text-sm leading-7">
                  <p>
                    <span className="text-violet-400">
                      const
                    </span>{" "}
                    <span className="text-sky-300">
                      developer
                    </span>{" "}
                    <span className="text-zinc-500">
                      =
                    </span>{" "}
                    <span className="text-zinc-300">
                      {"{"}
                    </span>
                  </p>

                  <p className="pl-5">
                    <span className="text-zinc-500">
                      name:
                    </span>{" "}
                    <span className="text-emerald-300">
                      "Kacper Agatowski"
                    </span>
                    ,
                  </p>

                  <p className="pl-5">
                    <span className="text-zinc-500">
                      focus:
                    </span>{" "}
                    <span className="text-emerald-300">
                      "Backend engineering"
                    </span>
                    ,
                  </p>

                  <p className="pl-5">
                    <span className="text-zinc-500">
                      building:
                    </span>{" "}
                    <span className="text-emerald-300">
                      "Reliable systems"
                    </span>
                    ,
                  </p>

                  <p className="pl-5">
                    <span className="text-zinc-500">
                      technologies:
                    </span>{" "}
                    <span className="text-zinc-300">
                      [
                    </span>
                  </p>

                  <p className="pl-10 text-emerald-300">
                    "APIs", "Databases", "Cloud",
                  </p>

                  <p className="pl-10 text-emerald-300">
                    "Security", "Applied AI"
                  </p>

                  <p className="pl-5 text-zinc-300">
                    ],
                  </p>

                  <p className="pl-5">
                    <span className="text-zinc-500">
                      location:
                    </span>{" "}
                    <span className="text-emerald-300">
                      "Dublin, Ireland"
                    </span>
                  </p>

                  <p className="text-zinc-300">
                    {"};"}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.button
          type="button"
          onClick={() => scrollToSection("about")}
          aria-label="Scroll to the About Me section"
          animate={{
            y: [0, 7, 0],
          }}
          transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute bottom-8 left-1/2 z-20 hidden -translate-x-1/2 items-center gap-2 rounded-full border border-zinc-300 bg-white/70 px-4 py-2 text-sm font-medium text-zinc-600 shadow-sm backdrop-blur-md transition hover:bg-white sm:flex"
        >
          Learn more about me

          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M6 9L12 15L18 9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </motion.button>
      </section>

      <About embedded />
      <Certificates embedded />
      <Contact embedded />
    </main>
  );
};

export default Home;
